<!-- 翻车机效能分析 - 完全按照原型图实现 -->
<template>
  <div class="car-dumper-efficiency nx-page nx-report-page">
    <!-- 页面标题区（统一报表骨架） -->
    <nx-report-header
      title="翻车机效能分析"
      subtitle="实时监控与分析翻车机作业效能数据"
    />

    <!-- 筛选区域 -->
    <div class="filter-area">
      <div class="filter-item">
        <label class="filter-label">时间范围</label>
        <el-date-picker
          v-model="queryParams.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          size="small"
          :picker-options="pickerOptions"
          style="width: 220px;"
          @change="handleSearch"
        ></el-date-picker>
      </div>
      <div class="filter-item">
        <label class="filter-label">设备选择</label>
        <el-select v-model="queryParams.equipmentNo" placeholder="全部设备" clearable size="small" style="width: 140px;" @change="handleSearch">
          <el-option label="全部设备" value=""></el-option>
          <el-option v-for="item in equipmentOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">煤种筛选</label>
        <el-select v-model="queryParams.coalType" placeholder="全部煤种" clearable size="small" style="width: 140px;" @change="handleSearch">
          <el-option label="全部煤种" value=""></el-option>
          <el-option v-for="item in coalTypeOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">效能阈值({{ queryParams.threshold }}%)</label>
        <el-slider v-model="queryParams.threshold" :min="50" :max="100" style="width: 150px;"></el-slider>
      </div>
    </div>

    <!-- 顶部指标卡片 -->
    <div class="kpi-cards">
      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">平均产能达成率</span>
          <div class="kpi-icon blue"><i class="el-icon-top"></i></div>
        </div>
        <div class="kpi-value">{{ statisticsData.avgCapacityRate || 0 }}%</div>
        <div class="kpi-trend">较上月 <span class="green">↑{{ statisticsData.avgCapacityRateChange || 0 }}%</span></div>
        <div class="kpi-progress">
          <div class="progress-bar blue" :style="{ width: (statisticsData.avgCapacityRate || 0) + '%' }"></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">设备综合利用率</span>
          <div class="kpi-icon green"><i class="el-icon-setting"></i></div>
        </div>
        <div class="kpi-value">{{ statisticsData.equipmentUtilizationRate || 0 }}%</div>
        <div class="kpi-trend">较上月 <span class="green">↑{{ statisticsData.equipmentUtilizationRateChange || 0 }}%</span></div>
        <div class="kpi-progress">
          <div class="progress-bar green" :style="{ width: (statisticsData.equipmentUtilizationRate || 0) + '%' }"></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">故障停机率</span>
          <div class="kpi-icon red"><i class="el-icon-warning"></i></div>
        </div>
        <div class="kpi-value">{{ statisticsData.failureRate || 0 }}%</div>
        <div class="kpi-trend">较上月 <span class="red">↑{{ statisticsData.failureRateChange || 0 }}%</span></div>
        <div class="kpi-progress">
          <div class="progress-bar gray">
            <div class="progress-segment red" :style="{ width: (statisticsData.failureRate || 0) * 5 + '%' }"></div>
          </div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">效能最优设备</span>
          <div class="kpi-icon purple"><i class="el-icon-trophy"></i></div>
        </div>
        <div class="kpi-value-small">{{ statisticsData.bestEquipment || '-' }}</div>
        <div class="kpi-sub">{{ statisticsData.bestEquipmentRate || 0 }}% 达成率</div>
        <div class="kpi-desc">连续{{ statisticsData.bestEquipmentMonths || 0 }}个月 领先</div>
        <div class="kpi-progress">
          <div class="progress-bar purple" :style="{ width: (statisticsData.bestEquipmentRate || 0) + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- 图表第一行 -->
    <div class="chart-row">
      <div class="chart-box left">
        <div class="chart-header">
          <span class="chart-title">实际vs理论产能对比</span>
          <div class="chart-tabs">
            <span :class="{ active: capacityView === 'month' }" @click="capacityView = 'month'">本月数据</span>
            <span :class="{ active: capacityView === 'compare' }" @click="capacityView = 'compare'">对比上月</span>
          </div>
        </div>
        <div id="capacityChart" class="chart-body"></div>
      </div>
      <div class="chart-box right">
        <div class="chart-header">
          <span class="chart-title">煤种作业效率</span>
          <span class="chart-tag">平均耗时</span>
        </div>
        <div id="coalChart" class="chart-body"></div>
      </div>
    </div>

    <!-- 图表第二行 -->
    <div class="chart-row single">
      <div class="chart-box full">
        <div class="chart-header">
          <span class="chart-title">设备利用率趋势</span>
          <div class="chart-actions">
            <span :class="{ active: trendView === '30days' }" @click="trendView = '30days'">近30天</span>
            <span :class="{ active: trendView === 'quarter' }" @click="trendView = 'quarter'">查看季度</span>
          </div>
        </div>
        <div id="trendChart" class="chart-body"></div>
      </div>
    </div>

    <!-- 详细效能数据表格 -->
    <div class="table-section">
      <div class="section-header">
        <span class="section-title">详细效能数据</span>
        <div class="section-actions">
          <el-button type="primary" size="small" @click="handleExport">导出报告</el-button>
          <el-button size="small" @click="showAdvancedFilter = true">高级筛选</el-button>
        </div>
      </div>
      <el-table :data="statisticsData.detailList || []" style="width: 100%" size="small" v-loading="tableLoading"
                :header-cell-style="{ background: '#F5F7FA', color: '#606266', fontWeight: '500' }">
        <el-table-column label="设备编号" prop="equipmentNo" width="100" align="center"></el-table-column>
        <el-table-column label="统计日期" prop="statDate" width="110" align="center"></el-table-column>
        <el-table-column label="日均翻转次数" prop="dailyFlipCount" width="120" align="center"></el-table-column>
        <el-table-column label="理论设计产能" prop="theoreticalCapacity" width="120" align="center"></el-table-column>
        <el-table-column label="实际完成产能" prop="actualCapacity" width="120" align="center"></el-table-column>
        <el-table-column label="设计产能达成率" width="140" align="center">
          <template slot-scope="scope">
            <div class="rate-cell">
              <span>{{ scope.row.capacityRate }}%</span>
              <div class="mini-progress">
                <div class="mini-bar" :style="{ width: scope.row.capacityRate + '%' }"></div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="单次作业耗时" width="120" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.timeTrend === 'up' ? 'trend-up' : 'trend-down'">
              {{ scope.row.singleOperationTime }}
              <i :class="scope.row.timeTrend === 'up' ? 'el-icon-top' : 'el-icon-bottom'"></i>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="故障停机时长" width="120" align="center">
          <template slot-scope="scope">
            <span :class="getFaultClass(scope.row.failureDuration)">{{ scope.row.failureDuration }}</span>
          </template>
        </el-table-column>
        <el-table-column label="设备利用率" width="120" align="center">
          <template slot-scope="scope">
            <div class="utilization-ring">
              <el-progress type="circle" :percentage="scope.row.utilizationRate" :width="50" :stroke-width="4"
                           :color="getUtilizationColor(scope.row.utilizationRate)"></el-progress>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-area">
        <span class="total-info">共{{ statisticsData.totalCount || 0 }}条，{{ queryParams.pageNum }}/{{ totalPages }}页</span>
        <el-pagination background layout="prev, pager, next" :total="statisticsData.totalCount || 0"
                       :page-size="5" :current-page="queryParams.pageNum" @current-change="handlePageChange">
        </el-pagination>
      </div>
    </div>

    <!-- 图表第三行：产能达成分析 + 煤种效率差异 -->
    <div class="chart-row">
      <div class="chart-box left">
        <div class="chart-header">
          <span class="chart-title">产能达成分分析</span>
        </div>
        <div class="capacity-analysis">
          <div class="analysis-item" v-for="item in statisticsData.capacityAnalysisList || []" :key="item.equipmentNo">
            <div class="analysis-header">
              <span class="analysis-name">{{ item.equipmentNo }}</span>
              <span class="analysis-rate">{{ item.capacityRate }}%</span>
            </div>
            <div class="analysis-progress">
              <div class="analysis-bar" :style="{ width: Math.min(item.capacityRate, 100) + '%' }"></div>
            </div>
            <div class="analysis-detail">
              <span>理论 {{ item.theoreticalCapacity }}次/日</span>
              <span>实际 {{ item.actualCapacity }}次/日</span>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-box right">
        <div class="chart-header">
          <span class="chart-title">煤种效率差异</span>
        </div>
        <div id="coalEfficiencyChart" class="chart-body"></div>
      </div>
    </div>

    <!-- 图表第四行：设备状态监控 -->
    <div class="chart-row single">
      <div class="chart-box full">
        <div class="chart-header">
          <span class="chart-title">设备状态监控</span>
        </div>
        <div class="status-monitor">
          <div class="monitor-section">
            <div class="monitor-title">主要故障类型</div>
            <div class="fault-bars">
              <div class="fault-item" v-for="item in statisticsData.failureTypeList || []" :key="item.typeName">
                <span class="fault-name">{{ item.typeName }}</span>
                <div class="fault-bar">
                  <div class="fault-fill" :style="{ width: item.percent + '%' }"></div>
                </div>
                <span class="fault-percent">{{ item.percent }}%</span>
              </div>
            </div>
          </div>
          <div class="monitor-section">
            <div class="monitor-title">平均修复时间</div>
            <div class="repair-bars">
              <div class="repair-item" v-for="item in statisticsData.repairTimeList || []" :key="item.typeName">
                <span class="repair-name">{{ item.typeName }}</span>
                <span class="repair-time">{{ item.avgTime }}h</span>
              </div>
            </div>
          </div>
          <div class="monitor-section">
            <div class="monitor-title">预防性维护建议</div>
            <div class="maintenance-list">
              <div class="maintenance-item" v-for="item in statisticsData.maintenanceList || []" :key="item.system">
                <span class="maintenance-system">{{ item.system }}：</span>
                <span class="maintenance-advice">{{ item.advice }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 效能改进跟踪表格 -->
    <div class="table-section">
      <div class="section-header">
        <span class="section-title">效能改进跟踪</span>
      </div>
      <el-table :data="statisticsData.improvementList || []" style="width: 100%" size="small"
                :header-cell-style="{ background: '#F5F7FA', color: '#606266', fontWeight: '500' }">
        <el-table-column label="改进措施" prop="measure" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="实施日期" prop="implDate" width="120" align="center"></el-table-column>
        <el-table-column label="实施前" prop="beforeValue" width="100" align="center"></el-table-column>
        <el-table-column label="实施后" prop="afterValue" width="100" align="center"></el-table-column>
        <el-table-column label="提升效果" prop="improvement" width="100" align="center">
          <template slot-scope="scope">
            <span class="improvement-text">{{ scope.row.improvement }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === '已完成' ? 'success' : 'warning'" size="small">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getStatistics, getEquipmentOptions, getCoalTypeOptions } from './api'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { getSearchTimePickerOptions } from '@/basic-common/util/common'

export default {
  name: 'car-dumper-efficiency',
  components: {
    NxReportHeader
  },
  data() {
    return {
      queryParams: {
        dateRange: [],
        equipmentNo: '',
        coalType: '',
        threshold: 70,
        pageNum: 1
      },
      pickerOptions: getSearchTimePickerOptions('daterange'),
      statisticsData: {},
      tableLoading: false,
      equipmentOptions: [],
      coalTypeOptions: [],
      capacityView: 'month',
      trendView: '30days',
      showAdvancedFilter: false
    }
  },
  computed: {
    totalPages() {
      return Math.ceil((this.statisticsData.totalCount || 0) / 5)
    }
  },
  mounted() {
    this.initDateRange()
    this.loadEquipmentOptions()
    this.loadCoalTypeOptions()
    this.loadStatistics()
  },
  methods: {
    initDateRange() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      this.queryParams.dateRange = [this.formatDate(start), this.formatDate(end)]
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    async loadEquipmentOptions() {
      try {
        const res = await getEquipmentOptions()
        if (res.data && res.data.data) {
          this.equipmentOptions = res.data.data
        }
      } catch (error) {
        console.error('获取设备列表失败:', error)
      }
    },
    async loadCoalTypeOptions() {
      try {
        const res = await getCoalTypeOptions()
        if (res.data && res.data.data) {
          this.coalTypeOptions = res.data.data
        }
      } catch (error) {
        console.error('获取煤种列表失败:', error)
      }
    },
    async loadStatistics() {
      this.tableLoading = true
      try {
        const res = await getStatistics(this.queryParams)
        if (res.data && res.data.success !== false) {
          this.statisticsData = res.data.data || {}
          this.$nextTick(() => {
            this.renderCapacityChart()
            this.renderCoalChart()
            this.renderTrendChart()
            this.renderCoalEfficiencyChart()
          })
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      } finally {
        this.tableLoading = false
      }
    },
    handleSearch() {
      this.queryParams.pageNum = 1
      this.loadStatistics()
    },
    handlePageChange(page) {
      this.queryParams.pageNum = page
      this.loadStatistics()
    },
    handleExport() {
      this.$message.success('导出成功')
    },
    getFaultClass(duration) {
      const hours = parseFloat(duration) || 0
      if (hours >= 3) return 'fault-high'
      if (hours >= 2) return 'fault-medium'
      return 'fault-low'
    },
    getUtilizationColor(rate) {
      if (rate >= 85) return '#22C55E'
      if (rate >= 70) return '#F59E0B'
      return '#EF4444'
    },
    renderCapacityChart() {
      const chartDom = document.getElementById('capacityChart')
      if (!chartDom) return
      const myChart = echarts.init(chartDom)
      const data = this.statisticsData.capacityCompareList || []

      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['理论设计产能', '实际完成产能', '产能达成率'], bottom: 0 },
        grid: { left: '3%', right: '5%', bottom: '12%', top: '10%', containLabel: true },
        xAxis: { type: 'category', data: data.map(d => d.equipmentNo), axisLine: { lineStyle: { color: '#E4E7ED' } } },
        yAxis: [
          { type: 'value', name: '翻转次数', max: 200, axisLine: { show: false }, splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } } },
          { type: 'value', name: '达成率', max: 100, axisLabel: { formatter: '{value}%' }, axisLine: { show: false }, splitLine: { show: false } }
        ],
        series: [
          { name: '理论设计产能', type: 'bar', barGap: '10%', data: data.map(d => d.theoreticalCapacity), itemStyle: { color: '#93C5FD' }, barWidth: 24 },
          { name: '实际完成产能', type: 'bar', data: data.map(d => d.actualCapacity), itemStyle: { color: '#3B82F6' }, barWidth: 24 },
          { name: '产能达成率', type: 'line', yAxisIndex: 1, data: data.map(d => d.capacityRate), itemStyle: { color: '#22C55E' }, lineStyle: { width: 2 }, symbol: 'circle', symbolSize: 6 }
        ]
      }
      myChart.setOption(option)
    },
    renderCoalChart() {
      const chartDom = document.getElementById('coalChart')
      if (!chartDom) return
      const myChart = echarts.init(chartDom)
      const data = this.statisticsData.coalTypeEfficiencyList || []

      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: '10%', right: '5%', bottom: '15%', top: '10%' },
        xAxis: { type: 'category', data: data.map(d => d.coalType), axisLine: { lineStyle: { color: '#E4E7ED' } } },
        yAxis: { type: 'value', name: '作业耗时(分钟)', max: 18, axisLine: { show: false }, splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } } },
        series: [{
          type: 'boxplot',
          data: data.map(d => [d.min, d.q1, d.median, d.q3, d.max]),
          itemStyle: { color: '#3B82F6', borderColor: '#3B82F6' }
        }]
      }
      myChart.setOption(option)
    },
    renderTrendChart() {
      const chartDom = document.getElementById('trendChart')
      if (!chartDom) return
      const myChart = echarts.init(chartDom)
      const data = this.statisticsData.utilizationTrendList || []

      const dateList = [...new Set(data.map(d => d.date))].sort()
      const equipmentList = ['翻车机A', '翻车机B', '翻车机C']
      const colors = ['#3B82F6', '#22C55E', '#8B5CF6']

      const series = equipmentList.map((eq, idx) => ({
        name: eq,
        type: 'line',
        data: dateList.map(date => {
          const item = data.find(d => d.date === date && d.equipmentNo === eq)
          return item ? item.utilizationRate : null
        }),
        itemStyle: { color: colors[idx] },
        smooth: true,
        symbol: 'circle',
        symbolSize: 6
      }))

      // 目标线
      series.push({
        name: '目标线',
        type: 'line',
        data: dateList.map(() => 85),
        itemStyle: { color: '#EF4444' },
        lineStyle: { type: 'dashed', width: 2 },
        symbol: 'none'
      })

      const option = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['翻车机A', '翻车机B', '翻车机C', '目标线'], bottom: 0 },
        grid: { left: '3%', right: '4%', bottom: '12%', top: '10%', containLabel: true },
        xAxis: { type: 'category', data: dateList, boundaryGap: false, axisLine: { lineStyle: { color: '#E4E7ED' } } },
        yAxis: { type: 'value', min: 60, max: 100, axisLabel: { formatter: '{value}%' }, axisLine: { show: false }, splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } } },
        series: series
      }
      myChart.setOption(option)
    },
    renderCoalEfficiencyChart() {
      const chartDom = document.getElementById('coalEfficiencyChart')
      if (!chartDom) return
      const myChart = echarts.init(chartDom)
      const data = this.statisticsData.coalEfficiencyList || []

      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: '{b}: {c}分钟' },
        grid: { left: '15%', right: '15%', bottom: '10%', top: '10%' },
        xAxis: { type: 'value', name: '分钟', axisLine: { show: false }, splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } } },
        yAxis: { type: 'category', data: data.map(d => d.coalType), axisLine: { lineStyle: { color: '#E4E7ED' } } },
        series: [{
          type: 'bar',
          data: data.map(d => ({
            value: d.avgTime,
            itemStyle: { color: d.isBest ? '#22C55E' : '#3B82F6' }
          })),
          barWidth: 20,
          label: {
            show: true,
            position: 'right',
            formatter: params => params.value + '分钟' + (data[params.dataIndex].isBest ? ' 最佳' : '')
          }
        }]
      }
      myChart.setOption(option)
    }
  }
}
</script>

<style lang="scss" scoped>
.car-dumper-efficiency {
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;

  .main-title {
    font-size: 24px;
    font-weight: 600;
    color: #1F2937;
    margin: 0 0 4px 0;
  }

  .sub-title {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
  }
}

.filter-area {
  display: flex;
  gap: 24px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .filter-label {
    font-size: 12px;
    color: #6B7280;
  }
}

.kpi-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.kpi-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  .kpi-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .kpi-title {
    font-size: 13px;
    color: #6B7280;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;

    &.blue { background: #EFF6FF; color: #3B82F6; }
    &.green { background: #ECFDF5; color: #22C55E; }
    &.red { background: #FEF2F2; color: #EF4444; }
    &.purple { background: #F5F3FF; color: #8B5CF6; }
  }

  .kpi-value {
    font-size: 28px;
    font-weight: 700;
    color: #1F2937;
    margin-bottom: 4px;
  }

  .kpi-value-small {
    font-size: 20px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 4px;
  }

  .kpi-trend {
    font-size: 12px;
    color: #6B7280;
    margin-bottom: 8px;

    .green { color: #22C55E; }
    .red { color: #EF4444; }
  }

  .kpi-sub {
    font-size: 12px;
    color: #6B7280;
    margin-bottom: 2px;
  }

  .kpi-desc {
    font-size: 11px;
    color: #9CA3AF;
    margin-bottom: 8px;
  }

  .kpi-progress {
    height: 4px;
    background: #E5E7EB;
    border-radius: 2px;
    overflow: hidden;

    .progress-bar {
      height: 100%;
      border-radius: 2px;

      &.blue { background: #3B82F6; }
      &.green { background: #22C55E; }
      &.purple { background: #8B5CF6; }
    }

    .progress-bar.gray {
      background: #E5E7EB;
      position: relative;

      .progress-segment.red {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: #EF4444;
        border-radius: 2px;
      }
    }
  }
}

.chart-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  &.single { display: block; }

  &.three-cols {
    .chart-box { flex: 1; }
  }

  .chart-box.left { flex: 3; }
  .chart-box.right { flex: 2; }
  .chart-box.full { width: 100%; }
}

.chart-box {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #F3F4F6;
  }

  .chart-title {
    font-size: 14px;
    font-weight: 500;
    color: #1F2937;
  }

  .chart-tabs {
    display: flex;
    gap: 4px;

    span {
      padding: 4px 12px;
      font-size: 12px;
      color: #6B7280;
      background: #F3F4F6;
      border-radius: 4px;
      cursor: pointer;

      &.active {
        color: #fff;
        background: #3B82F6;
      }
    }
  }

  .chart-tag {
    font-size: 12px;
    color: #6B7280;
    background: #F3F4F6;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .chart-actions {
    display: flex;
    gap: 8px;

    span {
      font-size: 12px;
      color: #6B7280;
      cursor: pointer;

      &.active { color: #3B82F6; }
    }
  }

  .chart-body {
    height: 280px;
    padding: 12px;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #F3F4F6;
  }

  .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #1F2937;
  }

  .section-actions {
    display: flex;
    gap: 8px;
  }

  .pagination-area {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-top: 1px solid #F3F4F6;
  }

  .total-info {
    font-size: 13px;
    color: #6B7280;
  }
}

.rate-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .mini-progress {
    flex: 1;
    height: 4px;
    background: #E5E7EB;
    border-radius: 2px;

    .mini-bar {
      height: 100%;
      background: #3B82F6;
      border-radius: 2px;
    }
  }
}

.trend-up { color: #22C55E; }
.trend-down { color: #EF4444; }

.fault-high { color: #EF4444; }
.fault-medium { color: #F59E0B; }
.fault-low { color: #22C55E; }

.improvement-text { color: #22C55E; font-weight: 500; }

.capacity-analysis {
  padding: 12px;

  .analysis-item {
    margin-bottom: 16px;

    &:last-child { margin-bottom: 0; }
  }

  .analysis-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .analysis-name {
    font-size: 13px;
    color: #374151;
  }

  .analysis-rate {
    font-size: 13px;
    font-weight: 500;
    color: #1F2937;
  }

  .analysis-progress {
    height: 8px;
    background: #E5E7EB;
    border-radius: 4px;
    margin-bottom: 4px;

    .analysis-bar {
      height: 100%;
      background: linear-gradient(90deg, #3B82F6, #22C55E);
      border-radius: 4px;
    }
  }

  .analysis-detail {
    display: flex;
    gap: 16px;
    font-size: 11px;
    color: #9CA3AF;
  }
}

.status-monitor {
  padding: 12px;
  display: flex;
  gap: 24px;

  .monitor-section {
    flex: 1;
  }

  .monitor-title {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .fault-bars {
    .fault-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }

    .fault-name {
      width: 70px;
      font-size: 11px;
      color: #6B7280;
    }

    .fault-bar {
      flex: 1;
      height: 6px;
      background: #E5E7EB;
      border-radius: 3px;

      .fault-fill {
        height: 100%;
        background: #3B82F6;
        border-radius: 3px;
      }
    }

    .fault-percent {
      width: 30px;
      font-size: 11px;
      color: #6B7280;
      text-align: right;
    }
  }

  .repair-bars {
    .repair-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px solid #F3F4F6;

      &:last-child { border-bottom: none; }
    }

    .repair-name {
      font-size: 11px;
      color: #6B7280;
    }

    .repair-time {
      font-size: 11px;
      color: #374151;
    }
  }

  .maintenance-list {
    .maintenance-item {
      font-size: 11px;
      color: #6B7280;
      margin-bottom: 4px;

      .maintenance-system {
        color: #374151;
        font-weight: 500;
      }
    }
  }
}
</style>
